import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import PageLayout from "components/layouts/PageLayout";
import AutomationExecution from "lib/api/AutomationExecution";
import {
  StatusBadge,
  ActionLabel,
  formatDateTime,
  formatDuration,
  formatMigrationData,
  automationStyles,
} from "components/automation/shared";

const ACTION_OPTIONS = [
  { value: "", label: "All actions" },
  { value: "provisioning", label: "Tenant Provisioning" },
  { value: "migration", label: "Data Migration" },
  { value: "password_reset", label: "Cloud User Password Reset" },
];

const STATUS_OPTIONS = [
  { value: "", label: "All statuses" },
  { value: "in_progress", label: "In Progress" },
  { value: "success", label: "Success" },
  { value: "failed", label: "Failed" },
  { value: "cancelled", label: "Cancelled" },
];

const AUTO_REFRESH_MS = 30000;

export default function AutomationExecutionList() {
  const [records, setRecords] = useState([]);
  const [nextToken, setNextToken] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [action, setAction] = useState("");
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [lastRefreshed, setLastRefreshed] = useState(null);
  const requestId = useRef(0);

  const load = useCallback(async () => {
    const id = ++requestId.current;
    setLoading(true);
    setError(null);
    try {
      const result = await AutomationExecution.get({ action, status, limit: 20 });
      if (id !== requestId.current) return;
      setRecords(result.data);
      setNextToken(result.next_token);
      setLastRefreshed(new Date());
    } catch (e) {
      if (id === requestId.current) setError(e.message);
    } finally {
      if (id === requestId.current) setLoading(false);
    }
  }, [action, status]);

  const loadMore = async () => {
    if (!nextToken) return;
    const id = ++requestId.current;
    setLoading(true);
    try {
      const result = await AutomationExecution.get({ action, status, limit: 20, next_token: nextToken });
      if (id !== requestId.current) return;
      setRecords((prev) => [...prev, ...result.data]);
      setNextToken(result.next_token);
    } catch (e) {
      if (id === requestId.current) setError(e.message);
    } finally {
      if (id === requestId.current) setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!autoRefresh) return undefined;
    const timer = setInterval(load, AUTO_REFRESH_MS);
    return () => clearInterval(timer);
  }, [autoRefresh, load]);

  const term = search.trim().toLowerCase();
  const visible = term
    ? records.filter((r) =>
        [r.client?.name, r.license_number, r.target_user, r.action_label, r.execution_id]
          .filter(Boolean)
          .some((v) => v.toLowerCase().includes(term))
      )
    : records;

  return (
    <PageLayout>
      <style>{automationStyles}</style>
      <h1>Cloud Automations</h1>
      <div className="hr hr--darkest"></div>
      <p style={{ fontSize: 13, color: "#6b7280", margin: "8px 0 12px" }}>
        Read-only view of AWS automation runs for tenant provisioning, data migration and cloud
        user password resets.
      </p>

      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end", marginBottom: 12 }}>
        <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 11, fontWeight: 700, color: "#6b7280", textTransform: "uppercase" }}>
          Action
          <select className="filter-bar__input" value={action} onChange={(e) => setAction(e.target.value)} style={{ height: 32, minWidth: 200 }}>
            {ACTION_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </label>
        <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 11, fontWeight: 700, color: "#6b7280", textTransform: "uppercase" }}>
          Status
          <select className="filter-bar__input" value={status} onChange={(e) => setStatus(e.target.value)} style={{ height: 32, minWidth: 150 }}>
            {STATUS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </label>
        <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 11, fontWeight: 700, color: "#6b7280", textTransform: "uppercase" }}>
          Search loaded rows
          <input
            className="filter-bar__input"
            placeholder="Client, license #, username…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ height: 32, minWidth: 240, padding: "0 8px" }}
          />
        </label>
        <div style={{ flex: 1 }} />
        <button className="automation-btn" onClick={load} disabled={loading}>
          {loading ? "Loading…" : "Refresh"}
        </button>
        <button
          className="automation-btn"
          onClick={() => setAutoRefresh((v) => !v)}
          style={autoRefresh ? { borderColor: "#6ee7b7", background: "#ecfdf5", color: "#065f46" } : undefined}
        >
          {autoRefresh ? "Auto Refresh: ON" : "Auto Refresh: OFF"}
        </button>
      </div>

      <div style={{ fontSize: 13, color: "#6b7280", marginBottom: 6 }}>
        {visible.length} run{visible.length !== 1 ? "s" : ""} shown
        {term && ` (filtered from ${records.length} loaded)`}
        {lastRefreshed && (
          <span style={{ marginLeft: 10, color: "#9ca3af" }}>
            Last refreshed {lastRefreshed.toLocaleTimeString()}
          </span>
        )}
      </div>

      {error && (
        <div style={{ padding: 10, marginBottom: 10, borderRadius: 6, background: "#fef2f2", color: "#991b1b", fontSize: 13 }}>
          Could not load automation runs: {error}
        </div>
      )}

      <div style={{ background: "#ffffff", borderRadius: 8, outline: "1px solid #e5e7eb", overflow: "auto", maxHeight: "calc(100vh - 330px)" }}>
        <table className="automation-table">
          <thead>
            <tr>
              <th>Action</th>
              <th>Client</th>
              <th>Status</th>
              <th>Started</th>
              <th>Duration</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {visible.map((r) => (
              <tr key={r.execution_id}>
                <td>
                  <ActionLabel action={r.action} label={r.action_label} />
                  {r.target_user && (
                    <div style={{ marginTop: 4, fontFamily: "monospace", fontSize: 12 }}>{r.target_user}</div>
                  )}
                  {r.action === "migration" && (
                    <div style={{ marginTop: 4, fontSize: 12, color: "#4b5563" }}>
                      {r.migration_data ? (
                        <>Data: {formatMigrationData(r.migration_data)}</>
                      ) : (
                        <span className="automation-muted">No Data Sync record</span>
                      )}
                    </div>
                  )}
                  {!r.parameters_known && (
                    <div className="automation-muted">Details unavailable, refresh to retry</div>
                  )}
                </td>
                <td>
                  {r.client ? (
                    <>
                      <Link to={`/client/${r.client.uuid}`} style={{ fontWeight: 500, color: "#111827" }}>
                        {r.client.name}
                      </Link>
                      <div className="automation-muted">License # {r.client.code}</div>
                    </>
                  ) : r.license_number ? (
                    <>
                      <span>License # {r.license_number}</span>
                      <div className="automation-muted">No matching client</div>
                    </>
                  ) : (
                    <span className="automation-muted">—</span>
                  )}
                </td>
                <td>
                  <StatusBadge group={r.status_group} status={r.status} />
                  {r.status_group === "in_progress" && r.current_step && (
                    <div className="automation-muted" style={{ marginTop: 4 }}>Step: {r.current_step}</div>
                  )}
                  {r.failure_message && (
                    <div className="automation-error" title={r.failure_message}>{r.failure_message}</div>
                  )}
                </td>
                <td style={{ whiteSpace: "nowrap" }}>{formatDateTime(r.started_at)}</td>
                <td style={{ whiteSpace: "nowrap" }}>{formatDuration(r.duration_seconds)}</td>
                <td>
                  <Link className="automation-btn" to={`/automation/${r.execution_id}`}>View</Link>
                </td>
              </tr>
            ))}
            {!loading && visible.length === 0 && (
              <tr>
                <td colSpan={6} style={{ textAlign: "center", padding: 24 }} className="automation-muted">
                  {nextToken
                    ? "No matching runs on this page. Use Load more to search older runs."
                    : "No automation runs found."}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div style={{ display: "flex", justifyContent: "center", marginTop: 12 }}>
        {nextToken && (
          <button className="automation-btn" onClick={loadMore} disabled={loading}>
            {loading ? "Loading…" : "Load more"}
          </button>
        )}
      </div>
    </PageLayout>
  );
}
